import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import FormRepeater from "../../form/FormRepeater";
import InfoChip from "../../InfoChip";
import PaymentRow from "./PaymentRow";
import {GetOffices} from "../../../api/offices/GetOffices";
import MessageError from "../../MessageError";
import Amount from '../../Amount';
import SkipActivation from '../../SkipActivation';
import SpecialtyItemsSection from "../../sell-specialty-items/SpecialtyItemsSection";
import { specialtyItemCount } from "../../sell-specialty-items/specialtyItemTotals";

const AddPayment = ({ formData, setFormData }) => {

    const { t } = useTranslation();

    const [error, setError] = useState('');

    const specialtyItems = formData.specialty_items ?? [];

    useEffect(() => {
        const fetchOffices = async () => {
            try {
                const { data : offices } = await GetOffices({limit: -1});
                const selectedOffice = offices.find(office => office.id === formData.office_id);
                if (selectedOffice) {
                    // Update formData when cards or office_id changes
                    setFormData(prevData => ({
                        ...prevData,
                        office_name: selectedOffice.name
                    }));
                }
            } catch (err) {
                console.log(err);
            }
        };
        if(!formData.office_name){
            fetchOffices();
        }
    }, []);

    useEffect(() => {

        const totalCollected = formData.payments.reduce((acc, payment) => acc + (Number(payment.amount) || 0), 0);

        setFormData(prevData => ({
            ...prevData,
            total_collected: totalCollected
        }));
    }, [formData.payments]);

    useEffect(()=> {
        if(formData.total_collected && (formData.total_cost - formData.total_collected !== 0)){
            setError(t('Remaining due must be equal 0.'));
        } else {
            setError('');
        }
    }, [formData.total_collected, formData.total_cost, formData.skip_activation_fee])

    return (
        <div className="step">
            <div className="container container-lg">

                <div className="mb-2">
                    <p style={{color: "var(--color-primary)"}}>{formData.office_name || t("Loading office...")}</p>
                    {formData.client_company_name &&
                        <p className="mb-2" style={{color: "var(--color-primary)"}}>{formData.client_company_name}</p>}
                    <InfoChip value={formData.cards.length} description={t('Cards')} className="mr-3"/>
                    {specialtyItems.length > 0 && (
                        <InfoChip
                            value={specialtyItemCount(specialtyItems)}
                            description={t('Specialty Items')}
                            className="mr-3"
                        />
                    )}
                    <InfoChip value={<Amount amount={formData.subtotal_cost} />} description={t('Subtotal')}/>
                </div>

                {formData.cards.length > 0 && (
                    <div className="mb-3">
                        <h5 className="mb-0">{t("Card")}</h5>
                        <hr className="size-3"/>
                        <FormRepeater
                            columns={[
                                t("Proxy Number"),
                                t("Card Type"),
                                t("Amount"),
                            ]}
                            RenderComponent={({rowData}) => (
                                <>
                                    <td>{rowData.processor_reference}</td>
                                    <td>{rowData.card_type}</td>
                                    <td><Amount amount={rowData.load_amount} /></td>
                                </>
                            )}
                            rowData={formData.cards}
                            hideAddRow
                            className="mb-3"
                        />
                    </div>
                )}

                {specialtyItems.length > 0 && (
                    <SpecialtyItemsSection lines={specialtyItems} taxRate={formData.provisional_tax_rate} readOnly />
                )}

                <div className="mb-3">
                    <h5 className="mb-0">{t("Payment")}</h5>
                    <hr className="size-3"/>
                </div>

                <FormRepeater
                    columns={[
                        t("Payment Name"),
                        t("Confirmation code"),
                        t("Amount"),
                        t("Action"),
                    ]}
                    maxRows={5}
                    addRowText={t("Add Payment")}
                    RenderComponent={PaymentRow}
                    onRowChange={payments => setFormData(prevData => ({
                        ...prevData,
                        payments
                    }))}
                    rowData={formData.payments}
                />

                {/* There is no activation fee without a card, so there is nothing
                    to skip. The value is left alone rather than reset, so a
                    choice made earlier survives adding cards back. */}
                {formData.cards.length > 0 && (
                    <SkipActivation initialValue={formData.skip_activation_fee} onChange={e => setFormData(prevData => ({
                        ...prevData,
                        skip_activation_fee: e.target.checked
                    }))}/>
                )}

                <div className="d-flex justify-content-end flex-wrap gap-1">
                    <InfoChip value={<Amount amount={formData.total_cost} />} description={t('Total Amount')}
                              className=""/>
                    <InfoChip value={<Amount amount={formData.total_service_fee} />} description={t('Activation Fee')}
                              className=""/>
                    <InfoChip value={<Amount amount={formData.specialty_items_tax || 0} />} description={t('Tax: Items')}
                              className=""/>
                    <InfoChip value={<Amount amount={formData.service_fee_tax || 0} />} description={t('Tax: Fee')}
                              className=""/>
                    <InfoChip value={<Amount amount={formData.total_collected} />} description={t('Total collected')}
                              className=""/>
                    <InfoChip value={<Amount amount={Number((formData.total_cost - formData.total_collected).toFixed(2))} />}
                              description={t('Remaining due')}/>
                </div>

                <MessageError error={error} className="text-center mt-1"/>

            </div>
        </div>
    );
};

export default AddPayment;
